import { useEffect, useRef, useState } from 'react';
import { getFaceLandmarker } from './mediapipeLoader';
import { renderFilter, FILTER_IDS } from './filters';

const TARGET_W = 640;
const TARGET_H = 480;
const TARGET_FPS = 24;

export function useFaceFilter({ activeFilterId = FILTER_IDS.NEUTRAL, enabled = true } = {}) {
  const [filteredVideoTrack, setFilteredVideoTrack] = useState(null);
  const [audioTrack, setAudioTrack] = useState(null);
  const [error, setError] = useState(null);
  const filterRef = useRef(activeFilterId);

  useEffect(() => {
    filterRef.current = activeFilterId;
  }, [activeFilterId]);

  useEffect(() => {
    if (!enabled) {
      setFilteredVideoTrack(null);
      setAudioTrack(null);
      return undefined;
    }

    let cancelled = false;
    let rawStream = null;
    let capturedStream = null;
    let videoEl = null;
    let rafId = 0;

    (async () => {
      try {
        rawStream = await navigator.mediaDevices.getUserMedia({
          video: { width: TARGET_W, height: TARGET_H },
          audio: true,
        });
        if (cancelled) {
          rawStream.getTracks().forEach((t) => t.stop());
          return;
        }

        const audio = rawStream.getAudioTracks()[0] || null;
        if (audio) setAudioTrack(audio);

        videoEl = document.createElement('video');
        videoEl.srcObject = rawStream;
        videoEl.playsInline = true;
        videoEl.muted = true;
        videoEl.autoplay = true;
        await videoEl.play();

        const canvasEl = document.createElement('canvas');
        canvasEl.width = TARGET_W;
        canvasEl.height = TARGET_H;
        const ctx = canvasEl.getContext('2d');

        const landmarker = await getFaceLandmarker();
        if (cancelled) return;

        capturedStream = canvasEl.captureStream(TARGET_FPS);
        const video = capturedStream.getVideoTracks()[0];
        if (video) setFilteredVideoTrack(video);

        const loop = () => {
          if (cancelled) return;
          rafId = requestAnimationFrame(loop);
          try {
            if (videoEl.readyState >= 2) {
              ctx.drawImage(videoEl, 0, 0, TARGET_W, TARGET_H);
              const result = landmarker.detectForVideo(videoEl, performance.now());
              const landmarks = result?.faceLandmarks?.[0];
              renderFilter(ctx, landmarks, filterRef.current, TARGET_W, TARGET_H);
            }
          } catch (_) {
            // per-frame errors are non-fatal; keep looping
          }
        };
        rafId = requestAnimationFrame(loop);
      } catch (e) {
        console.error('useFaceFilter init failed:', e);
        if (!cancelled) setError(e?.message || String(e));
      }
    })();

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (rawStream) rawStream.getTracks().forEach((t) => t.stop());
      if (capturedStream) capturedStream.getTracks().forEach((t) => t.stop());
      if (videoEl) {
        videoEl.pause();
        videoEl.srcObject = null;
      }
      setFilteredVideoTrack(null);
      setAudioTrack(null);
    };
  }, [enabled]);

  return { filteredVideoTrack, audioTrack, error };
}
